import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Clicking the backdrop closes the modal unless this is false. */
  dismissible?: boolean;
}

/** The one modal. Replaces four generic Modal copies and ~36 hand-rolled
 *  fixed backdrops: portal, Escape to close, focus moved inside on open. */
export const Modal: React.FC<ModalProps> = ({
  open, onClose, title, children, footer, size = 'md', dismissible = true,
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && dismissible) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); previous?.focus?.(); };
  }, [open, dismissible, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="ds-overlay" onMouseDown={e => { if (dismissible && e.target === e.currentTarget) onClose(); }}>
      <div
        ref={dialogRef}
        className={`ds-modal ds-modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="ds-modal__header">
          <h2 id={titleId} className="ds-modal__title">{title}</h2>
          <button type="button" className="ds-modal__close" onClick={onClose} aria-label={t('common.close', 'Close')}><X size={16} /></button>
        </div>
        <div className="ds-modal__body">{children}</div>
        {footer && <div className="ds-modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
